import React from "react";
import { Link } from "react-router-dom";
import { useBrand, useMarket, useTenantPath } from "@/lib/brand";
import { lbl } from "@/lib/terms";
import FloatingContact from "@/components/FloatingContact";
import PageviewTracker from "@/components/PageviewTracker";

export default function SiteFooter() {
  const brand = useBrand();
  const market = useMarket();
  const biz = brand.business;
  const tenantPath = useTenantPath();
  const legalLinks = [
    brand.fees ? { label: "Fees", to: tenantPath("fees") } : null,
    brand.legal?.terms ? { label: "Terms", to: tenantPath("terms") } : null,
    brand.legal?.privacy ? { label: "Privacy", to: tenantPath("privacy") } : null,
    brand.legal?.cookies ? { label: "Cookies", to: tenantPath("cookies") } : null,
  ].filter(Boolean);
  return (
    <>
    <PageviewTracker />
    <FloatingContact />
    <footer data-testid="site-footer" className="mt-24 border-t border-border bg-primary text-primary-foreground">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14 grid gap-10 md:grid-cols-4">
        <div>
          {(() => {
            const darkLogo = brand.assets?.logo_dark_url;
            const primaryLogo = brand.assets?.logo_url;
            // No logo of their own means their name, set as a wordmark. It
            // used to mean "/agency-logo.png": a real customer's mark
            // in another agency's footer.
            if (!darkLogo && !primaryLogo) {
              return (
                <span
                  className="text-xl md:text-2xl font-semibold tracking-tight text-primary-foreground"
                  data-testid="footer-logo"
                >
                  {biz.name}
                </span>
              );
            }
            // Dark-mode logo strategy:
            //  1. If tenant supplies a dedicated dark-bg logo, use it with a
            //     `screen` blend so any residual light PNG background dissolves
            //     into the dark footer.
            //  2. Otherwise, invert the primary logo — clean for monochrome
            //     wordmarks (e.g. The first customer) but destroys complex logos.
            return darkLogo ? (
              <img
                src={darkLogo}
                alt={biz.name}
                className="h-20 md:h-24 w-auto max-w-[180px] md:max-w-[220px] object-contain"
                data-testid="footer-logo"
              />
            ) : (
              <img
                src={primaryLogo}
                alt={biz.name}
                className="h-20 md:h-24 w-auto max-w-[180px] md:max-w-[220px] object-contain invert brightness-0 opacity-95"
                data-testid="footer-logo"
              />
            );
          })()}
          <p className="mt-5 text-sm text-primary-foreground/70 max-w-xs">
            {brand.business?.footer_intro
              || `${lbl("footer_default_intro", market)} ${brand.regions_covered_prose}.`}
          </p>
        </div>
        <div className="text-sm space-y-2">
          <div className="uppercase tracking-[0.06em] text-xs text-primary-foreground/60 mb-3">Contact</div>
          {biz.phone && <div>{biz.phone}</div>}
          {biz.email && <div>{biz.email}</div>}
          {biz.address && <div className="text-primary-foreground/70 pt-1">{biz.address}</div>}
        </div>
        <div className="text-sm space-y-2">
          <div className="uppercase tracking-[0.06em] text-xs text-primary-foreground/60 mb-3">Regulated by</div>
          {(brand.regulatory?.footer_lines || []).map((line, i) => <div key={i}>{line}</div>)}
        </div>
        {brand.areas_served?.length > 0 && (
          <div className="text-sm">
            <div className="uppercase tracking-[0.06em] text-xs text-primary-foreground/60 mb-3">Areas we cover</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {brand.areas_served.map((a) => (
                <Link
                  key={a}
                  to={tenantPath(`area/${a.toLowerCase().replace(/\s+/g, "-")}`)}
                  className="text-primary-foreground/80 hover:text-primary-foreground underline-offset-2 hover:underline"
                >
                  {a}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="border-t border-primary-foreground/10 py-5 text-center text-xs text-primary-foreground/50 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
        <span>© {new Date().getFullYear()} {biz.name} · {biz.tagline}</span>
        {legalLinks.length > 0 && (
          <span className="flex items-center gap-3">
            {legalLinks.map((l) => (
              <Link key={l.to} to={l.to} className="hover:text-primary-foreground/80 underline underline-offset-2">
                {l.label}
              </Link>
            ))}
          </span>
        )}
      </div>
    </footer>
    </>
  );
}
